import type { PortfolioItem } from '@/types/portfolio'

/** '지금도 운영 중' 이라고 말할 수 있는 항목만. 개수·로고 띠 같은 파생 값은 전부 이걸 써요. */
export const livePortfolio = (items: PortfolioItem[]): PortfolioItem[] =>
  items.filter((item) => !item.upcoming)

/**
 * 포트폴리오의 원본은 DB(어드민 → 포트폴리오 탭)이고, 웹은 `GET /portfolio` 로 읽어요.
 * 아래는 API 가 응답하지 못할 때 쓰는 폴백 스냅샷 — apps/api `api/services/portfolio.py` 의
 * `DEFAULT_PORTFOLIO` 와 같은 값이에요(의도된 중복, 드리프트는 감수).
 *
 * 빌드 시점 SEO 본문(`lib/seo-static.ts` → `<noscript>`·`/llms.txt`)도 이 스냅샷을 읽어요 —
 * 빌드 때는 DB 를 못 읽으니, 어드민에서 항목을 고치면 크롤러용 본문은 다음 배포에 따라와요.
 * 대신 방문자가 보는 화면(포트폴리오 카드·Hero 로고 띠·About KPI)은 바로 반영돼요.
 *
 * 각 설명은 실제 제품 저장소 기준으로 정리했어요. 대부분 운영 중이지만 출시 전인 것도 있어요 —
 * 그런 항목은 문안에 '출시 예정'을 명시해요(api 가 `upcoming` 플래그와 함께 검증해요).
 */
export const FALLBACK_PORTFOLIO: PortfolioItem[] = [
  {
    id: 'bitpilot',
    name: '비트파일럿',
    tagline: '여러 거래소를 연결해 조건 기반으로 자동매매하는 가상자산 투자 플랫폼이에요',
    description:
      '바이낸스·빗썸·업비트·OKX 같은 여러 거래소를 API로 연결하고, 자금은 거래소에 그대로 둔 채 잔고와 주문만 동기화하는 비수탁 구조예요. 코드 없이 지표 조건을 조합해 만든 매매 전략을 24시간 자동으로 실행하고, 현물과 선물(레버리지)을 같은 화면에서 다뤄요. 전략 마켓플레이스·백테스트·실현손익 리더보드로 성과를 확인할 수 있고, 웹 대시보드와 모바일 앱을 함께 제공해요.',
    category: 'own',
    platform: ['web', 'app'],
    url: 'https://bitpilot.co',
    cover: '/portfolio/bitpilot.webp',
    logo: '/portfolio/logos/bitpilot.png',
  },
  {
    id: 'meshcore-concierge',
    name: '스테이라인',
    tagline: '예약 링크 하나로 시작하는 숙박 호스트용 AI 게스트 컨시어지예요',
    description:
      '투숙객은 앱 설치나 로그인 없이 발급된 링크만 열면, 호스트가 등록한 와이파이·체크인·주변 안내를 근거로 답하는 AI 챗봇과 대화할 수 있어요. 예약을 등록하면 게스트 링크가 자동으로 만들어지고, 호스트는 관리자 콘솔에서 객실 배정 보드·예약 타임라인·전자 계약·리포트를 처리해요. 숙소를 여러 곳 등록해 팀원을 역할별 권한으로 초대할 수 있고, Channex 연동으로 OTA 채널의 객실·요금·예약을 양방향으로 동기화해요. 한국어·영어·중국어·일본어 다국어 응대를 지원해요.',
    category: 'own',
    platform: ['web'],
    url: 'https://stayline.io',
    cover: '/portfolio/meshcore-concierge.webp',
    logo: '/portfolio/logos/meshcore-concierge.png',
  },
  {
    id: 'workly',
    name: '워클리',
    tagline: '문서, 프로젝트, 회계까지 한 곳에서 일하는 팀용 협업 워크스페이스예요',
    description:
      '업로드한 문서를 벡터로 검색하고 근거를 들어 답하는 지식 채팅에, 보드·프로젝트·마일스톤·캘린더·할 일·회계·자산·위키를 하나의 워크스페이스로 통합했어요. 자주 쓰는 업무는 AI ‘스킬’로 저장해 워크스페이스 지식을 참조하며 실행하고, 마켓플레이스에서 조직 안의 스킬을 공유하거나 가져다 쓸 수 있어요. 화면과 알림 문구는 한국어·영어·베트남어를 같은 수준으로 지원해요.',
    category: 'own',
    platform: ['web'],
    url: 'https://vvorkly.com',
    cover: '/portfolio/workly.webp',
    logo: '/portfolio/logos/workly.svg',
  },
  {
    id: 'clair',
    name: '찐친소',
    tagline: '지인의 추천으로 만나는, 신뢰 기반 소개팅 서비스예요',
    description:
      '직접 가입하거나 지인이 대신 소개해 주는 두 가지 방식을 지원하고, 실명 본인확인을 통과한 사람만 매칭 자격을 얻어요. 앱과 웹의 ‘소개 마당’에서 상대를 탐색하고 쪽지로 대화를 이어가며, 앱 설치 없이도 지인을 소개하거나 초대 링크를 보낼 수 있어요. 카카오톡 ID는 양쪽이 모두 공개에 동의한 순간에만 서로에게 열리고, 그 전까지 실명·사진·연락처는 공개되지 않도록 설계했어요.',
    category: 'own',
    platform: ['web', 'app'],
    url: 'https://jjinchinso.com',
    cover: '/portfolio/clair.webp',
    logo: '/portfolio/logos/clair.png',
  },
  {
    id: 'nangtul',
    name: '냉털',
    tagline: '냉장고 재료를 고르면 오늘 만들 요리를 추천해 주는 AI 레시피예요',
    description:
      '가지고 있는 재료를 골라 담으면 AI가 알러지·회피 재료를 걸러 한식 위주의 레시피 카드를 만들어 줘요. 쉬운 한 끼부터 다른 스타일, 업그레이드 버전까지 여러 제안을 함께 보여주고, 내 냉장고 저장·즐겨찾기·만든 기록으로 점점 취향에 맞게 개인화돼요. 웹과 모바일 앱을 함께 제공해요.',
    category: 'own',
    platform: ['web', 'app'],
    url: 'https://nangtul.com',
    cover: '/portfolio/nangtul.webp',
    logo: '/portfolio/logos/nangtul.png',
  },
  {
    id: 'duri',
    name: '두리',
    tagline: '초대 코드로 연결된 커플을 위한, 둘만의 공간 앱이에요',
    description:
      '가입할 때 본인확인을 거치고, 한 사람이 초대 코드를 보내고 상대가 입력하면 두 사람이 하나의 커플로 연결돼요. 1:1 채팅, 공유 캘린더, 데이트 추억, 지도, 위시리스트, 기념일 D-day를 한 앱에서 관리하고, 하루의 순간은 ‘모먼트’로 남겨 날짜별로 되돌아볼 수 있어요. 구글 캘린더를 연동해 일정을 함께 보고, 기념일·일정 리마인더를 푸시로 받아요.',
    category: 'own',
    platform: ['app'],
    url: 'https://durii.us',
    cover: '/portfolio/duri.webp',
    logo: '/portfolio/logos/duri.svg',
  },
  {
    id: 'timebox-planner',
    name: '실록',
    tagline: '하루를 시간 블록으로 설계하고 기록으로 남기는 1인용 생산성 앱이에요',
    description:
      '오늘 할 일을 목록으로만 두지 않고 06시–22시 30분 단위 타임라인(집중·업무·개인·휴식)에 배치해 하루를 설계해요. 오늘의 우선순위 3가지, 드래그로 타임라인에 올리는 브레인덤프, 주간·월간 뷰, 포모도로 타이머와 방해 기록, 블록 회고로 하루를 닫고, 프로젝트·칸반과 위키로 여러 날에 걸친 일을 이어가요. 대시보드로 시간 사용을 돌아보고, 내장 AI 비서에게 자연어로 일정 추가·정리를 맡길 수 있어요. 웹과 모바일 앱을 함께 제공해요.',
    category: 'own',
    platform: ['web', 'app'],
    url: 'https://shillok.com',
    cover: '/portfolio/timebox-planner.webp',
    logo: '/portfolio/logos/timebox-planner.svg',
  },
  {
    id: 'key-news',
    name: '헤드라이너',
    tagline: '키워드를 한 번에 스무 개까지 넣고 네이버 뉴스를 모아 보는 모니터링 도구예요',
    description:
      '여러 주제를 동시에 좇는 기자·마케터·리서처를 위한 도구예요. 키워드를 최대 20개까지 넣으면 네이버 뉴스를 한 번에 검색해 탭과 카드로 정리해 주고, 정렬·표시 개수·호출 간격을 직접 맞출 수 있어요. 로그인하면 키워드 세트와 화면 설정이 저장되고, AI 요약으로 기사 묶음을 빠르게 훑어볼 수 있어요. 골라낸 기사는 클리핑으로 묶어 링크 하나로 공유하는데, 그 링크는 SNS·검색 크롤러가 자바스크립트 없이도 읽도록 서버에서 미리 그려 내려가요.',
    category: 'own',
    platform: ['web'],
    url: 'https://headliner.kr',
    cover: '/portfolio/key-news.webp',
    logo: '/portfolio/logos/key-news.png',
  },
  {
    id: 'highlight',
    name: '하이라이트',
    tagline: '종이책 페이지를 찍으면 AI가 문장을 골라 주는 독서기록 앱이에요 (출시 예정)',
    description:
      '타이핑 없이 종이책을 기록하는 앱이에요. 읽다가 마음에 남은 페이지를 찍으면 AI가 문장을 추출해 추천해 주고, 그중 고른 문장을 메모와 함께 도서별로 아카이브해요. AI가 만든 문장과 코멘트에는 늘 AI 라벨을 함께 붙여서 내가 쓴 것과 구분돼요. 매일 지난 기록을 다시 꺼내 보여주는 알림도 있어요. 구글·애플 계정으로 로그인하고, iOS 16과 안드로이드 10 이상을 지원해요. 아직 출시 전이라 스토어에서 내려받을 수는 없고, 링크는 소개 페이지로 이어져요.',
    category: 'own',
    platform: ['app'],
    upcoming: true,
    url: 'https://highlight-web.bucks.dchain-connect.com',
    cover: '/portfolio/highlight.webp',
    logo: '/portfolio/logos/highlight.svg',
  },
]
